import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, Chip, Alert, AlertTitle, Fade,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HotelIcon from '@mui/icons-material/Hotel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import EventIcon from '@mui/icons-material/Event';
import SectionHeader from './SectionHeader';
import DetailSection from './DetailSection';
import { GradientBackground } from './styled';

const RecoveryDetail = ({ futureContext, recentContext, onBack }) => {
  const theme = useTheme();
  const recovery = futureContext?.recovery;
  const legacyRecoveryHours = futureContext?.recommended_recovery_hours;
  
  const baseRecoveryHours = recovery?.overall_recovery_hours || legacyRecoveryHours || 24;
  const totalRecoveryHours = recovery?.total_recommended_rest_hours || baseRecoveryHours;
  const intensityLevel = recovery?.intensity_level || 'moderate';
  const mostFatigued = recovery?.most_fatigued_muscles || [];
  const suggestedFocus = recovery?.suggested_next_focus || [];
  const tips = recovery?.recovery_tips || [];
  const suggestDeload = recovery?.suggested_deload;
  const suggestRest = recovery?.suggested_rest_day;

  const intensityColors = { light: 'success', moderate: 'info', hard: 'warning', very_hard: 'error' };

  return (
    <Fade in timeout={300}>
      <Box>
        <SectionHeader icon={<HealthAndSafetyIcon />} title="Recovery & Next Steps" onBack={onBack} />
        
        <GradientBackground color="info" sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={6}>
              <Typography variant="overline" color="text.secondary">Recommended Recovery</Typography>
              <Typography variant="h2" fontWeight={800} color="info.main">
                {totalRecoveryHours}
                <Typography component="span" variant="h5" color="text.secondary" sx={{ ml: 1 }}>hours</Typography>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="overline" color="text.secondary">Session Intensity</Typography>
              <Typography 
                variant="h3" 
                fontWeight={700} 
                color={`${intensityColors[intensityLevel] || 'text'}.main`}
                sx={{ textTransform: 'capitalize' }}
              >
                {intensityLevel.replace(/_/g, ' ')}
              </Typography>
            </Grid>
          </Grid>
        </GradientBackground>

        {suggestDeload && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Deload Week Recommended</AlertTitle>
            Consider reducing volume by 40-50% this week while maintaining intensity to allow for recovery.
          </Alert>
        )}

        {suggestRest && (
          <Alert severity="info" icon={<HotelIcon />} sx={{ mb: 2 }}>
            <AlertTitle>Rest Day Suggested</AlertTitle>
            Your body could benefit from a rest day before your next training session.
          </Alert>
        )}

        <Grid container spacing={2}>
          {mostFatigued.length > 0 && (
            <Grid item xs={12} sm={6}>
              <DetailSection title="Most Fatigued" icon={<BatteryAlertIcon sx={{ fontSize: 18 }} />} color="warning">
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {mostFatigued.map((muscle, idx) => (
                    <Chip key={idx} label={muscle} variant="outlined" color="warning" size="small" sx={{ textTransform: 'capitalize' }} />
                  ))}
                </Box>
              </DetailSection>
            </Grid>
          )}
          {suggestedFocus.length > 0 && (
            <Grid item xs={12} sm={6}>
              <DetailSection title="Suggested Next Focus" icon={<FitnessCenterIcon sx={{ fontSize: 18 }} />} color="success">
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {suggestedFocus.map((focus, idx) => (
                    <Chip key={idx} label={focus} variant="outlined" color="info" size="small" sx={{ textTransform: 'capitalize' }} />
                  ))}
                </Box>
              </DetailSection>
            </Grid>
          )}
        </Grid>

        {tips.length > 0 && (
          <DetailSection title="Recovery Tips" icon={<LightbulbOutlinedIcon sx={{ fontSize: 18 }} />} color="info">
            <List dense disablePadding>
              {tips.map((tip, idx) => (
                <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckCircleIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary={tip} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </DetailSection>
        )}

        {futureContext?.next_scheduled_event && (
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex', 
            alignItems: 'center',
            gap: 1.5
          }}>
            <EventIcon color="primary" />
            <Box>
              <Typography variant="body2" color="text.secondary">Next Scheduled:</Typography>
              <Typography variant="subtitle2" fontWeight={600}>{futureContext.next_scheduled_event}</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  );
};

RecoveryDetail.propTypes = {
  futureContext: PropTypes.object,
  recentContext: PropTypes.object,
  onBack: PropTypes.func.isRequired,
};

export default RecoveryDetail;